/* ================================================================
   Moteur de synchronisation — Gestion Formation (CEJ CONCEPT)
   Étape 4 : Rejoue les mutations en attente contre le serveur

   Ordre de traitement : chronologique (plus ancienne en premier).
   En cas d'erreur réseau sur une mutation, on arrête le traitement
   (inutile de continuer si le serveur est encore inaccessible).
   ================================================================ */

import {
  getPendingMutations,
  removeMutation,
  updateMutationStatus,
} from './offlineDB';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// ── Labels humains par endpoint + méthode ────────────────────
const LABELS = {
  '/presences'     : { POST: 'Présence enregistrée',  PUT: 'Présence modifiée',    PATCH: 'Présence modifiée',    DELETE: 'Présence supprimée' },
  '/inscriptions'  : { POST: 'Inscription ajoutée',   PUT: 'Inscription modifiée', DELETE: 'Inscription supprimée' },
  '/sessions'      : { POST: 'Session créée',          PUT: 'Session modifiée' },
  '/session-groupes': { POST: 'Groupe créé',           PUT: 'Groupe modifié',       DELETE: 'Groupe supprimé' },
  '/clients'       : { POST: 'Client créé',            PUT: 'Client modifié',       DELETE: 'Client supprimé' },
  '/devis'         : { POST: 'Devis créé',             PUT: 'Devis modifié',        DELETE: 'Devis supprimé' },
  '/stagiaires'    : { POST: 'Stagiaire créé',         PUT: 'Stagiaire modifié',    DELETE: 'Stagiaire supprimé' },
  '/formateurs'    : { POST: 'Formateur créé',         PUT: 'Formateur modifié' },
  '/formations'    : { POST: 'Formation créée',        PUT: 'Formation modifiée' },
};

/**
 * Retourne un libellé lisible pour une mutation.
 * Ex : { method:'PUT', endpoint:'/presences/42' } → 'Présence modifiée'
 */
export function describeMutation(mutation) {
  // Extraire le chemin de base (sans ID ni query string)
  const segments  = mutation.endpoint.replace(/^\//, '').split('?')[0].split('/');
  const basePath  = '/' + segments[0];
  const label     = LABELS[basePath]?.[mutation.method];
  return label || `${mutation.method} ${mutation.endpoint.split('?')[0]}`;
}

/**
 * Formate le timestamp d'une mutation de façon lisible.
 */
export function formatMutationAge(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min  = Math.floor(diff / 60_000);
  const h    = Math.floor(min / 60);
  const d    = Math.floor(h / 24);
  if (d > 0)   return `il y a ${d}j`;
  if (h > 0)   return `il y a ${h}h`;
  if (min > 0) return `il y a ${min} min`;
  return 'à l\'instant';
}

/**
 * Rejoue toutes les mutations en attente contre le serveur.
 *
 * @param {string}   token      — JWT de l'utilisateur courant
 * @param {Function} onProgress — callback({ current, total, mutation, phase })
 *                                phase: 'syncing' | 'done'
 * @returns {{ total, synced, failed, conflicts }} — résumé de la synchro
 */
export async function syncPendingMutations(token, onProgress) {
  const mutations = await getPendingMutations();
  const results   = { total: mutations.length, synced: 0, failed: 0, conflicts: 0 };

  if (mutations.length === 0) {
    onProgress?.({ current: 0, total: 0, mutation: null, phase: 'done' });
    return results;
  }

  for (let i = 0; i < mutations.length; i++) {
    const mutation = mutations[i];
    onProgress?.({ current: i, total: mutations.length, mutation, phase: 'syncing' });

    try {
      const url = `${API_BASE}${mutation.endpoint}`;
      const res = await fetch(url, {
        method : mutation.method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body  : mutation.body ? JSON.stringify(mutation.body) : undefined,
        cache : 'no-store',
      });

      if (res.ok) {
        // ✓ Succès → retirer de la file
        await removeMutation(mutation.id);
        results.synced++;
      } else if (res.status === 409) {
        // ⚠ Conflit (modification concurrente côté serveur)
        await updateMutationStatus(mutation.id, 'conflict', 'Modification concurrente — vérifiez les données');
        results.conflicts++;
      } else {
        // ✕ Erreur serveur (validation, droits, etc.)
        let errMsg = `Erreur ${res.status}`;
        try {
          const json = await res.json();
          errMsg = json.error || json.message || errMsg;
        } catch { /* ignore */ }
        await updateMutationStatus(mutation.id, 'error', errMsg);
        results.failed++;
      }
    } catch (_networkErr) {
      // Serveur toujours inaccessible → marquer en erreur et s'arrêter
      await updateMutationStatus(mutation.id, 'error', 'Serveur inaccessible — réessayez plus tard');
      results.failed++;
      // Inutile de continuer pour les mutations suivantes
      break;
    }
  }

  onProgress?.({ current: mutations.length, total: mutations.length, mutation: null, phase: 'done' });
  return results;
}
